import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  Tabs,
  Tab
} from '@mui/material';
import { BASE_URL_API } from '../configs/urls';


function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function InspectionsDashboard({ refresh }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${BASE_URL_API}/inspections/stats`);
        setStats(res.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching stats:', err);
        setError('Error al cargar estadísticas');
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
        Panel de Indicadores de Inspecciones
      </Typography>

      <Grid container spacing={3}>

        {/* Principales violaciones */}
        <Grid item xs={12} lg={6}>
          <Card elevation={3} sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" color="textSecondary" gutterBottom>
                Principales Violaciones
              </Typography>
              {stats.topViolations.length > 0 ? (
                <List dense>
                  {stats.topViolations.map((violation, index) => (
                    <React.Fragment key={violation.fed_vio_code}>
                      <ListItem>
                        <ListItemText
                          primary={`${index + 1}. ${violation.fed_vio_code} - ${violation.violation_category}`}
                          secondary={`${violation.count} ocurrencias - ${violation.section_desc}`}
                          secondaryTypographyProps={{ noWrap: true }}
                        />
                      </ListItem>
                      {index < stats.topViolations.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography variant="body1">No hay datos de violaciones</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Top 10 Unidades y Operadores */}
        <Grid item xs={12}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" color="textSecondary" gutterBottom>
                Top 10 - Unidades y Operadores
              </Typography>
              
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tabValue} onChange={handleTabChange}>
                  <Tab label="Unidades con más violaciones" />
                  <Tab label="Operadores con más violaciones" />
                  <Tab label="Locaciones con más violaciones" />
                </Tabs>
              </Box>
              
              <TabPanel value={tabValue} index={0}>
                <List dense>
                  {stats.topVehicles.map((vehicle, index) => (
                    <React.Fragment key={vehicle.vehicle_id || index}>
                      <ListItem>
                        <ListItemText
                          primary={`${index + 1}. ${vehicle.unit_number} - ${vehicle.vehicle_id || 'N/A'}`}
                          secondary={`${vehicle.violation_count} violaciones`}
                        />
                      </ListItem>
                      {index < stats.topVehicles.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </TabPanel>
              
              <TabPanel value={tabValue} index={1}>
                <List dense>
                  {stats.topDrivers.map((driver, index) => (
                    <React.Fragment key={driver.driver_name || index}>
                      <ListItem>
                        <ListItemText
                          primary={`${index + 1}. ${driver.driver_name || 'N/A'}`}
                          secondary={`${driver.violation_count} violaciones`}
                        />
                      </ListItem>
                      {index < stats.topDrivers.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </TabPanel>
              <TabPanel value={tabValue} index={2}>
                <List dense>
                  {stats.topLocations.map((location, index) => (
                    <React.Fragment key={location.inspection_location || index}>
                      <ListItem>
                        <ListItemText
                          primary={`${index + 1}. ${location.inspection_location || 'N/A'}`}
                          secondary={`${location.violation_count} violaciones`}
                        />
                      </ListItem>
                      {index < stats.topLocations.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </TabPanel>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}